import { NavigateFunction } from 'react-router-dom';
import { MouseEvent } from 'react';

import { isExternalLink } from 'lib/strings/strings';
import { isKeyboardModifierClick } from 'lib/browser';
import {
  STIMULUS_RESOURCE_TYPES,
  GUIDED_RESEARCH,
  WORKSHEET,
  VIDEO,
  AUDIO,
  INQUISITIVE_PAGE,
  INTERACTIVE,
  EBOOK_INTERACTIVE,
} from 'global/constants';
import { windowOpen } from 'lib/screen';
import { NEW_TAB_SLUG_PREFIXES } from 'webclient.constants';
import { Props } from './content-card.constants';
import { convert2Array } from 'lib/arrays/arrays';
import {
  openAccessDeniedModal,
  openAdminModal,
  openInactiveAccountModal,
  openJoinModal,
  openSignInModal,
} from 'approot/global-notifications/global-notification.utils';
import { ModalKey } from 'approot/shared/modal/modal.constants';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';

const MODAL_SIGNIN = `modal=${ModalKey.SIGNIN}`;
const MODAL_JOIN = `modal=${ModalKey.JOIN}`;
const MODAL_DENIED = `modal=${ModalKey.DENIED}`;
const MODAL_INACTIVE = `modal=${ModalKey.INACTIVE_ACCOUNT}`;
const MODAL_ADMIN = `modal=${ModalKey.ADMIN}`;

const PERMISSION_DENIED_LINK = null;

export function generateContentLink(
  isViewable: boolean,
  type: string,
  slug: string | null | undefined,
  link: string | null | undefined,
  prependDomain?: boolean
) {
  if (
    type === GUIDED_RESEARCH ||
    type === VIDEO ||
    type === AUDIO ||
    type === INTERACTIVE ||
    type === EBOOK_INTERACTIVE
  ) {
    return prependDomain ? getRegionalisedLink(slug || '') : slug;
  }
  if (!isViewable) {
    return PERMISSION_DENIED_LINK;
  }
  if (type === WORKSHEET || STIMULUS_RESOURCE_TYPES.includes(type)) {
    return link;
  }
}

export function goOrShowModal(link: string): boolean {
  if (link.includes(MODAL_JOIN)) {
    openJoinModal();
    return false;
  } else if (link.includes(MODAL_SIGNIN)) {
    openSignInModal();
    return false;
  } else if (link.includes(MODAL_ADMIN)) {
    openAdminModal();
    return false;
  } else if (link.includes(MODAL_DENIED)) {
    openAccessDeniedModal();
    return false;
  } else if (link.includes(MODAL_INACTIVE)) {
    openInactiveAccountModal();
    return false;
  }
  return true;
}

export function isNewTabSlug(link: string) {
  const linkPrefix = link.substring(0, link.indexOf('/', 1) + 1);
  return NEW_TAB_SLUG_PREFIXES.includes(linkPrefix);
}

export function shouldOpenInNewTab(link: string) {
  return isNewTabSlug(link) || isExternalLink(link);
}

export function createExternalUrl(link: string) {
  if (isNewTabSlug(link)) {
    return `/${process.env.REACT_APP_REGION}${link}?referrer=${INQUISITIVE_PAGE}`;
  }
  return link;
}

export const clickLink = ({
  navigate,
  link,
}: {
  navigate: NavigateFunction;
  link?: string;
}) => {
  if (!link) {
    return console.error(
      `The missing link... link:${link}. This should not happen.`
    );
  }

  if (link.includes(MODAL_JOIN)) {
    return openJoinModal();
  } else if (link.includes(MODAL_SIGNIN)) {
    return openSignInModal();
  } else if (link.includes(MODAL_ADMIN)) {
    return openAdminModal();
  } else if (link.includes(MODAL_DENIED)) {
    return openAccessDeniedModal();
  } else if (link.includes(MODAL_INACTIVE)) {
    return openInactiveAccountModal();
  }

  const linkPrefix = link.substring(0, link.indexOf('/', 1) + 1);
  const newTab = NEW_TAB_SLUG_PREFIXES.includes(linkPrefix);

  if (isExternalLink(link)) {
    openInWindow(link);
  } else if (newTab) {
    openInWindow(`/${process.env.REACT_APP_REGION}${link}`);
  } else {
    navigate(link);
  }
};

export const openInWindow = (url: string) => {
  if (!url) return;
  windowOpen(url);
};

export const getScreenReaderCardLinkText = (
  pillText: string | undefined,
  title: string,
  details?: Props['detail'],
  meta?: Props['metaLine'],
  featureBadgeText?: string
) => {
  const textParts: string[] = [];
  textParts.push(pillText ? `${pillText}: ${title}` : title);
  if (details && details.length > 0) textParts.push(...convert2Array(details));
  if (meta && meta.length > 0) textParts.push(...convert2Array(meta));
  if (featureBadgeText) textParts.push(featureBadgeText);
  return textParts.join(', ');
};

export function openLinkAsAuthenticatedUser({
  event,
  href,
  navigate,
}: {
  event: MouseEvent<HTMLAnchorElement>;
  href: string;
  navigate: NavigateFunction;
}) {
  if (isKeyboardModifierClick(event)) return;

  event.preventDefault();
  if (!goOrShowModal(href)) {
    return false;
  }
  clickLink({ navigate, link: href });
}
